import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Trash2 } from 'lucide-react';
import {
  ATTENDANCE_LEDGER_KINDS,
  type AttendanceLedgerKind,
  type StaffMember,
} from '@tailoring-crm/shared';
import {
  useAttendanceLedger,
  useCreateLedgerEntry,
  useDeleteLedgerEntry,
} from '../../lib/attendance/use-attendance.js';
import { EmptyState } from '../feedback/empty-state.js';
import { Button, Input, Label, ListSkeleton, useToast } from '@tailoring-crm/ui';

/**
 * Overtime and permission, over a stretch of days.
 *
 * A RANGE, not a day, and that is the difference between this panel and the roll call beside
 * it. "Is Lakshmi in yet?" is a question about this morning; "how much overtime did we run up
 * last month?" is the question this exists for, and it cannot be asked one day at a time.
 *
 * The totals come down WITH the list and are not added up from the rows on screen — the server
 * totals the whole range, so page two of a month reports a month.
 *
 * There is no rupee figure anywhere here, deliberately. An entry says how long somebody stayed
 * and why; what that is worth is resolved by the payroll run against the rate card, which is
 * also what lets a karigar read their own ledger at all.
 */

/** Minutes as "2h 30m", which is how a shop says it out loud. */
export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${String(rest)}m`;
  if (rest === 0) return `${String(hours)}h`;
  return `${String(hours)}h ${String(rest)}m`;
}

export interface AttendanceLedgerPanelProps {
  from: string;
  to: string;
  /** The roster, for the person picker. Empty for a karigar, who may only read their own. */
  roster: readonly StaffMember[];
  canWrite: boolean;
}

export function AttendanceLedgerPanel({
  from,
  to,
  roster,
  canWrite,
}: AttendanceLedgerPanelProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [kind, setKind] = useState<AttendanceLedgerKind | ''>('');
  const [adding, setAdding] = useState(false);

  const ledger = useAttendanceLedger({ from, to, ...(kind ? { kind } : {}) });
  const remove = useDeleteLedgerEntry();

  const items = ledger.data?.items ?? [];
  const totals = ledger.data?.totals;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ledger-kind">{t('attendance.ledger.kindLabel')}</Label>
          <select
            id="ledger-kind"
            value={kind}
            onChange={(event) => setKind(event.target.value as AttendanceLedgerKind | '')}
            className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
          >
            <option value="">{t('attendance.ledger.allKinds')}</option>
            {ATTENDANCE_LEDGER_KINDS.map((one) => (
              <option key={one} value={one}>
                {t(`attendance.ledger.kind.${one}`)}
              </option>
            ))}
          </select>
        </div>

        {canWrite ? (
          <Button onClick={() => setAdding(!adding)}>{t('attendance.ledger.add')}</Button>
        ) : null}
      </div>

      {/* Range-wide, from the server. Never counted from the rows below. */}
      {totals ? (
        <dl className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-border p-3">
            <dt className="text-sm text-muted-foreground">
              {t('attendance.ledger.kind.overtime')}
            </dt>
            <dd className="text-lg font-semibold">{formatMinutes(totals.overtimeMinutes)}</dd>
          </div>
          <div className="rounded-lg border border-border p-3">
            <dt className="text-sm text-muted-foreground">
              {t('attendance.ledger.kind.permission')}
            </dt>
            <dd className="text-lg font-semibold">{formatMinutes(totals.permissionMinutes)}</dd>
          </div>
        </dl>
      ) : null}

      {adding && canWrite ? (
        <LedgerEntryForm roster={roster} defaultDay={to} onDone={() => setAdding(false)} />
      ) : null}

      {ledger.isPending ? <ListSkeleton rows={3} /> : null}

      {!ledger.isPending && items.length === 0 ? (
        <EmptyState
          icon={Clock}
          title={t('attendance.ledger.emptyTitle')}
          description={t('attendance.ledger.emptyDescription')}
        />
      ) : null}

      {items.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {items.map((entry) => (
            <li
              key={entry.id}
              className="flex items-start gap-3 rounded-md border border-border p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {entry.staffName} · {formatMinutes(entry.minutes)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t(`attendance.ledger.kind.${entry.kind}`)} · {entry.day}
                  {entry.reason ? ` · ${entry.reason}` : ''}
                </p>
                {entry.recordedByName ? (
                  <p className="text-xs text-muted-foreground">
                    {t('attendance.ledger.recordedBy', { name: entry.recordedByName })}
                  </p>
                ) : null}
              </div>
              {canWrite ? (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('attendance.ledger.remove')}
                  onClick={() => {
                    void (async () => {
                      try {
                        await remove.mutateAsync(entry.id);
                        toast({ title: t('attendance.ledger.removed'), variant: 'success' });
                      } catch {
                        toast({ title: t('error.unexpected'), variant: 'destructive' });
                      }
                    })();
                  }}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function LedgerEntryForm({
  roster,
  defaultDay,
  onDone,
}: {
  roster: readonly StaffMember[];
  defaultDay: string;
  onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const create = useCreateLedgerEntry();

  const [staffUserId, setStaffUserId] = useState(roster[0]?.userId ?? '');
  const [day, setDay] = useState(defaultDay);
  const [kind, setKind] = useState<AttendanceLedgerKind>('overtime');
  const [hours, setHours] = useState('1');
  const [minutes, setMinutes] = useState('0');
  const [reason, setReason] = useState('');

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    // Entered as hours and minutes because that is how a shop says it; sent as one figure
    // because that is what the ledger stores and what a total can be taken of.
    const total = Number(hours) * 60 + Number(minutes);
    void (async () => {
      try {
        await create.mutateAsync({
          staffUserId,
          day,
          kind,
          minutes: total,
          reason: reason.trim() || null,
        });
        toast({ title: t('attendance.ledger.added'), variant: 'success' });
        onDone();
      } catch (error) {
        toast({
          title: error instanceof Error ? error.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  return (
    <form className="flex flex-col gap-3 rounded-md border border-border p-3" onSubmit={submit}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ledger-staff">{t('attendance.ledger.person')}</Label>
        <select
          id="ledger-staff"
          value={staffUserId}
          onChange={(event) => setStaffUserId(event.target.value)}
          className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
        >
          {roster.map((member) => (
            <option key={member.userId} value={member.userId}>
              {member.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ledger-day">{t('attendance.ledger.day')}</Label>
          <Input
            id="ledger-day"
            type="date"
            value={day}
            onChange={(event) => setDay(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ledger-entry-kind">{t('attendance.ledger.kindLabel')}</Label>
          <select
            id="ledger-entry-kind"
            value={kind}
            onChange={(event) => setKind(event.target.value as AttendanceLedgerKind)}
            className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
          >
            {ATTENDANCE_LEDGER_KINDS.map((one) => (
              <option key={one} value={one}>
                {t(`attendance.ledger.kind.${one}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ledger-hours">{t('attendance.ledger.hours')}</Label>
          <Input
            id="ledger-hours"
            type="number"
            min={0}
            max={16}
            value={hours}
            onChange={(event) => setHours(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ledger-minutes">{t('attendance.ledger.minutes')}</Label>
          <Input
            id="ledger-minutes"
            type="number"
            min={0}
            max={59}
            step={5}
            value={minutes}
            onChange={(event) => setMinutes(event.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ledger-reason">{t('attendance.ledger.reason')}</Label>
        <Input
          id="ledger-reason"
          value={reason}
          maxLength={300}
          placeholder={t('attendance.ledger.reasonPlaceholder')}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={create.isPending}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

export default AttendanceLedgerPanel;
